import React, { useRef, useState } from 'react';
import { useFiles } from '../../hooks/useFiles';
import { useIsMobile } from '../../hooks/useMobile';
import { Button } from '@exiftrator/shared/components/ui';

function UploadScreen() {
  const { addFiles } = useFiles();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const isMobile = useIsMobile();

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleFileSelect = (e) => {
    addFiles(e.target.files);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Mobile: no aspect ratio, min-h-[200px], safe-area padding
  // Desktop: aspect-[4/3] ratio
  const dropZoneClassName = isMobile
    ? 'min-h-[200px] pb-safe'
    : 'aspect-[4/3]';

  const containerPadding = isMobile ? 'p-4' : 'p-8';
  const iconSize = isMobile ? '48' : '64';
  const titleSize = isMobile ? 'text-[16px]' : 'text-[18px]';
  const textSize = isMobile ? 'text-[12px]' : 'text-[13px]';
  const infoTextSize = isMobile ? 'text-[10px]' : 'text-[11px]';
  const buttonSize = isMobile ? 'md' : 'lg';

  return (
    <div className={`flex-1 flex items-center justify-center ${containerPadding}`}>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          w-full max-w-[500px] ${dropZoneClassName} rounded-lg border-2 border-dashed
          flex flex-col items-center justify-center gap-4 sm:gap-6 p-4 sm:p-8
          transition-all duration-200 cursor-pointer
          ${isDragging
            ? 'border-accent-primary bg-accent-primary/10 scale-[1.02]'
            : 'border-border-default hover:border-border-default hover:bg-bg-hover'
          }
        `}
        onClick={handleClick}
      >
        <div className={`transition-transform duration-200 ${isDragging ? 'scale-110' : ''}`}>
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-colors ${isDragging ? 'text-accent-primary' : 'text-text-tertiary'}`}
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>

        <div className="text-center">
          <h2 className={`${titleSize} font-bold mb-2 transition-colors ${isDragging ? 'text-accent-primary' : 'text-text-primary'}`}>
            {isDragging ? 'Drop files here' : 'Drag and drop images'}
          </h2>
          <p className={`${textSize} text-text-tertiary`}>
            or click to browse your files
          </p>
        </div>

        <div className={`flex items-center gap-4 text-text-tertiary ${infoTextSize}`}>
          <span>Supports JPG, PNG, TIFF, HEIC</span>
        </div>

        <Button
          variant="default"
          size={buttonSize}
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        >
          Select Files
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    </div>
  );
}

export default UploadScreen;
